import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import GradientCircularProgress from '../../components/common/GradientCircularProgress';
import TableCard, { TableColumn } from '../../components/common/TableCard';
import useBackendApi from '../../hooks/useBackendApi';
import { RequestOptions } from '../../types/httpRequests';
import { OrganizationType } from '../../types/organization';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

export default function OrganizationsList(): JSX.Element {
  const [organizations, setOrganizations] = useState<OrganizationType[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);

  const { isLoading, apiFetchRequest } = useBackendApi();
  const navigate = useNavigate();

  const endpointUrl = `${backendURL}/o/`;

  const fail = () => {
    setErrorText('Something went wrong. Please try again.');
    setOrganizations([]);
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      const apiRequestOptions: RequestOptions = {
        endpoint: endpointUrl,
        method: 'GET',
      };

      try {
        const data = await apiFetchRequest(apiRequestOptions);

        if (!data || data.status !== 200) {
          navigate('/404.html');
          return;
        }

        const body = data.body;

        if (body?.status_code === 401 || body?.status_code === 403) {
          navigate('/401.html');
          return;
        }

        if (body?.message === 'NO_ORGS') {
          setOrganizations([]);
          setErrorText(null);
          return;
        }
        if (body?.message && body.message !== 'SUCCESS') {
          fail();
          return;
        }

        if (Array.isArray(body?.organizations)) {
          setOrganizations(body.organizations);
          setErrorText(null);
        } else {
          fail();
        }
      } catch {
        fail();
      }
    };

    fetchOrganizations();
  }, [apiFetchRequest, endpointUrl, navigate]);

  const onClickOrganization = (pathName: string) => {
    navigate(`/o/${encodeURIComponent(pathName)}`);
  };

  if (isLoading && !errorText && organizations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography variant="body1" color="error">
          {errorText}
        </Typography>
      </Box>
    );
  }

  if (!organizations.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography variant="h6">No organizations available</Typography>
      </Box>
    );
  }

  const columns: TableColumn<OrganizationType>[] = [
    { label: 'Name', render: (org) => org.name },
    { label: 'Description', render: (org) => org.description || '-' },
  ];

  return (
    <TableCard
      title="Organizations"
      rows={organizations}
      columns={columns}
      getRowKey={(org) => org.org_id ?? org.path_name}
      onRowClick={(org) => onClickOrganization(org.path_name)}
    />
  );
}
